import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import CryptoJS from 'crypto-js';
import { useAuth } from '../../context/AuthContext';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import './LoginModal.css';

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const handleSuccess = onLoginSuccess || onClose;
  const { loginWithPostingKey, loginWithKeychain, loginWithSavedAccount, loginWithEmail, getSavedAccounts, removeAccount, user, checkPromptUpdateStoredKey } = useAuth();
  const [activeTab, setActiveTab] = useState('web2'); // 'postingkey', 'keychain', or 'web2'
  
  // Posting key login state (legacy support)
  const [username, setUsername] = useState('');
  const [postingKey, setPostingKey] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  
  // Web2 login state
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [hiveUsername, setHiveUsername] = useState('');
  const [hivePostingKey, setHivePostingKey] = useState(''); // For non-custodial encryption
  const [confirmPassword, setConfirmPassword] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  
  // Saved accounts state
  const [savedAccounts, setSavedAccounts] = useState({});
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [quickLoginPin, setQuickLoginPin] = useState('');
  const [isUpdatingKey, setIsUpdatingKey] = useState(false);
  const [newPostingKeyInput, setNewPostingKeyInput] = useState('');
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSavedAccounts(getSavedAccounts());
    }
  }, [isOpen, getSavedAccounts]);

  useBodyScrollLock(isOpen || showPinModal);

  if (!isOpen) return null;

  const handlePostingKeyLogin = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    if (!postingKey.trim()) {
      setError('Posting key is required');
      return;
    }

    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) {
      setError('PIN must be exactly 6 digits');
      return;
    }

    if (pin !== confirmPin) {
      setError('PINs do not match');
      return;
    }

    setIsLoading(true);

    try {
      await loginWithPostingKey(username.trim().toLowerCase(), postingKey.trim(), pin);
      handleSuccess();
      // Reset form
      setUsername('');
      setPostingKey('');
      setPin('');
      setConfirmPin('');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeychainLogin = async () => {
    setError('');
    setIsLoading(true);

    try {
      await loginWithKeychain();
      handleSuccess();
    } catch (err) {
      setError(err.message || 'Keychain login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWeb2Login = async (e) => {
    e.preventDefault();
    setError('');

    if (!emailOrUsername.trim() || !password.trim()) {
      setError('Email/Username and password are required');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emailOrUsername: emailOrUsername.trim(),
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Non-custodial decryption: Decrypt the posting key on CLIENT side
      if (data.encryptedKey) {
        try {
          const decryptedBytes = CryptoJS.AES.decrypt(data.encryptedKey, password);
          const decryptedKey = decryptedBytes.toString(CryptoJS.enc.Utf8);
          
          if (!decryptedKey) {
            throw new Error('Failed to decrypt key - wrong password');
          }

          // Store decrypted key in sessionStorage (not localStorage - security!)
          sessionStorage.setItem('hive_posting_key', decryptedKey);
          sessionStorage.setItem('hive_username', data.username);
        } catch (decryptError) {
          console.error('Decryption error:', decryptError);
          setError('Failed to decrypt your keys. Please check your password.');
          setIsLoading(false);
          return;
        }
      }

      await loginWithEmail(emailOrUsername.trim(), password);
      handleSuccess();
      // Reset form
      setEmailOrUsername('');
      setPassword('');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await fetch(`/api/user/check-username/${username}`);
      const data = await response.json();
      setUsernameAvailable(data.available);
    } catch (err) {
      console.error('Error checking username:', err);
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleWeb2SignUp = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email.trim() || !password.trim() || !hiveUsername.trim() || !hivePostingKey.trim()) {
      setError('All fields are required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!usernameAvailable) {
      setError('Please choose an available Hive username');
      return;
    }

    // Validate posting key format (should start with '5')
    if (!hivePostingKey.startsWith('5') || hivePostingKey.length < 50) {
      setError('Invalid Hive posting key format');
      return;
    }

    setIsLoading(true);

    try {
      // Non-custodial encryption: Encrypt posting key with user's password on CLIENT side
      const encryptedKey = CryptoJS.AES.encrypt(hivePostingKey, password).toString();

      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          hiveUsername: hiveUsername.trim().toLowerCase(),
          encryptedKey // Send encrypted key (server never sees plaintext key)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Store decrypted key in sessionStorage for this session only
      sessionStorage.setItem('hive_posting_key', hivePostingKey);
      sessionStorage.setItem('hive_username', hiveUsername.trim().toLowerCase());

      // Auto login after signup
      await loginWithEmail(email.trim(), password);
      handleSuccess();
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAccountClick = async (username) => {
    const account = savedAccounts[username];
    
    // Handle Keychain accounts - login directly without PIN
    if (account.type === 'keychain') {
      setError('');
      setIsLoading(true);
      try {
        await loginWithSavedAccount(username);
        handleSuccess();
      } catch (err) {
        setError(err.message || 'Keychain login failed');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Handle posting key accounts - show PIN modal
    setSelectedAccount(username);
    setShowPinModal(true);
    setQuickLoginPin('');
    setIsUpdatingKey(false);
    setNewPostingKeyInput('');
    setError('');
  };

  const handleQuickLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (quickLoginPin.length !== 6) {
      setError('PIN must be 6 digits');
      return;
    }

    setIsLoading(true);
    try {
      if (isUpdatingKey) {
        if (!newPostingKeyInput.trim()) {
          setError('Please enter your new posting key');
          setIsLoading(false);
          return;
        }
        await loginWithPostingKey(selectedAccount, newPostingKeyInput.trim(), quickLoginPin);
        setShowPinModal(false);
        handleSuccess();
        if (checkPromptUpdateStoredKey) {
          checkPromptUpdateStoredKey(selectedAccount, newPostingKeyInput.trim());
        }
      } else {
        await loginWithSavedAccount(selectedAccount, quickLoginPin);
        setShowPinModal(false);
        handleSuccess();
      }
    } catch (err) {
      if (err.code === 'KEY_OUTDATED') {
        setIsUpdatingKey(true);
        setError('Your stored posting key is no longer valid or changed on Hive. Please enter your new posting key below.');
      } else {
        setError(err.message || 'Invalid PIN');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = (username) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove @${username}? You'll need to login again with posting key and PIN.`
    );
    if (confirmed) {
      removeAccount(username);
      setSavedAccounts(getSavedAccounts());
    }
  };

  const modalContent = (
    <>
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="login-modal">
          <div className="modal-header">
            <h2>Login to Flexus</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

          <div className="modal-tabs">
            <button
              className={`tab-btn ${activeTab === 'web2' ? 'active' : ''}`}
              onClick={() => setActiveTab('web2')}
            >
              Email / Social
            </button>
            <button
              className={`tab-btn ${activeTab === 'postingkey' ? 'active' : ''}`}
              onClick={() => setActiveTab('postingkey')}
            >
              Posting Key
            </button>
            <button
              className={`tab-btn ${activeTab === 'keychain' ? 'active' : ''}`}
              onClick={() => setActiveTab('keychain')}
            >
              Keychain
            </button>
          </div>

          <div className="modal-content">
            {/* Saved Accounts - Visible in all tabs */}
            {savedAccounts && Object.keys(savedAccounts).length > 0 && (
              <div className="saved-accounts-section">
                <h3>Saved Accounts</h3>
                <div className="saved-accounts-list">
                  {Object.keys(savedAccounts).map(username => {
                    const account = savedAccounts[username];
                    const accountType = account.type || 'postingkey';
                    return (
                      <div key={username} className="saved-account-item">
                        <button
                          type="button"
                          className="saved-account-btn"
                          onClick={() => handleAccountClick(username)}
                        >
                          <img 
                            src={`https://images.hive.blog/u/${username}/avatar/small`}
                            alt={username}
                            className="saved-account-avatar"
                            onError={(e) => {
                              e.target.src = `https://ui-avatars.com/api/?name=${username}&background=random&size=32`;
                            }}
                          />
                          <span className="saved-account-name">
                            {accountType === 'keychain' ? '⚡' : accountType === 'web2' ? '📧' : '🔑'} @{username}
                          </span>
                          {user?.username === username && <span className="checkmark">✓</span>}
                        </button>
                        <button
                          type="button"
                          className="delete-account-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAccount(username);
                          }}
                          title="Remove account"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="divider">or use another login method</div>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            {activeTab === 'postingkey' ? (
              <>
                <form onSubmit={handlePostingKeyLogin}>
                  <div className="info-box">
                    Your posting key will be encrypted with your PIN and stored locally. 
                    Session expires after 6 hours of inactivity or 24 hours maximum.
                  </div>

                  <div className="form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="your-hive-username"
                      disabled={isLoading}
                      autoComplete="username"
                    />
                  </div>

                  <div className="form-group">
                    <label>Posting Key</label>
                    <input
                      type="password"
                      value={postingKey}
                      onChange={(e) => setPostingKey(e.target.value)}
                      placeholder="5..."
                      disabled={isLoading}
                      autoComplete="off"
                    />
                    <span className="hint">Your private posting key (starts with 5)</span>
                  </div>

                  <div className="form-group">
                    <label>Create PIN (6 digits)</label>
                    <input
                      type="password"
                      inputMode="numeric"
                      className="pin-input"
                      value={pin}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setPin(value);
                      }}
                      placeholder="••••••"
                      maxLength={6}
                      disabled={isLoading}
                      autoComplete="off"
                    />
                    <span className="hint">You'll use this PIN to unlock your account</span>
                  </div>

                  <div className="form-group">
                    <label>Confirm PIN</label>
                    <input
                      type="password"
                      inputMode="numeric"
                      className="pin-input"
                      value={confirmPin}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setConfirmPin(value);
                      }}
                      placeholder="••••••"
                      maxLength={6}
                      disabled={isLoading}
                      autoComplete="off"
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="login-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Logging in...' : 'Login with Posting Key'}
                  </button>
                </form>
              </>
            ) : activeTab === 'keychain' ? (
              <div className="keychain-section">
                <div className="keychain-logo">🔑</div>
                <p>
                  Click the button below to login with Hive Keychain extension.<br />
                  You'll need to <b>select account and approve the login</b> in the Keychain popup.
                </p>

                <div className="info-box">
                  Don't have Keychain? Install it from{' '}
                  <a href="https://hive-keychain.com/" target="_blank" rel="noopener noreferrer">
                    hive-keychain.com
                  </a>
                </div>

                <button 
                  className="login-btn"
                  onClick={handleKeychainLogin}
                  disabled={isLoading}
                >
                  {isLoading ? 'Waiting for approval...' : 'Login with Keychain'}
                </button>
              </div>
            ) : (
              <div className="web2-section">
                {!isSignUp ? (
                  <form onSubmit={handleWeb2Login}>
                    <div className="info-box">
                      🌐 Login with your email and password. No crypto knowledge needed!
                    </div>

                    <div className="form-group">
                      <label>Email or Hive Username</label>
                      <input
                        type="text"
                        value={emailOrUsername}
                        onChange={(e) => setEmailOrUsername(e.target.value)}
                        placeholder="your@email.com or @username"
                        disabled={isLoading}
                        autoComplete="username"
                      />
                    </div>

                    <div className="form-group">
                      <label>Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Your password"
                        disabled={isLoading}
                        autoComplete="current-password"
                      />
                    </div>

                    <button 
                      type="submit" 
                      className="login-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Logging in...' : 'Login'}
                    </button>

                    <div className="auth-footer">
                      <p>Don't have an account?{' '}
                        <button 
                          type="button"
                          className="link-button"
                          onClick={() => setIsSignUp(true)}
                        >
                          Sign Up
                        </button>
                      </p>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleWeb2SignUp}>
                    <div className="info-box warning">
                      ⚠️ <strong>Important:</strong> Your Hive username cannot be changed after creation. Choose carefully!
                    </div>

                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        disabled={isLoading}
                        autoComplete="email"
                      />
                    </div>

                    <div className="form-group">
                      <label>Choose Your Hive Username</label>
                      <input
                        type="text"
                        value={hiveUsername}
                        onChange={(e) => {
                          const value = e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, '');
                          setHiveUsername(value);
                          if (value.length >= 3) {
                            checkUsernameAvailability(value);
                          } else {
                            setUsernameAvailable(null);
                          }
                        }}
                        placeholder="yourname"
                        disabled={isLoading}
                        autoComplete="off"
                      />
                      {checkingUsername && <span className="hint">Checking...</span>}
                      {usernameAvailable === true && (
                        <span className="hint success">✓ Username available!</span>
                      )}
                      {usernameAvailable === false && (
                        <span className="hint error">✗ Username taken</span>
                      )}
                      <span className="hint">3-16 characters, lowercase letters, numbers, dots and dashes only</span>
                    </div>

                    <div className="form-group">
                      <label>Hive Posting Key 🔐</label>
                      <input
                        type="password"
                        value={hivePostingKey}
                        onChange={(e) => setHivePostingKey(e.target.value)}
                        placeholder="5..."
                        disabled={isLoading}
                        autoComplete="off"
                      />
                      <span className="hint">Your posting key will be encrypted with your password and never sent unencrypted</span>
                    </div>

                    <div className="form-group">
                      <label>Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        disabled={isLoading}
                        autoComplete="new-password"
                      />
                      <span className="hint">Used to encrypt your posting key (non-custodial)</span>
                    </div>

                    <div className="form-group">
                      <label>Confirm Password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your password"
                        disabled={isLoading}
                        autoComplete="new-password"
                      />
                    </div>

                    <button 
                      type="submit" 
                      className="login-btn"
                      disabled={isLoading || !usernameAvailable}
                    >
                      {isLoading ? 'Creating Account...' : 'Create Account'}
                    </button>

                    <div className="auth-footer">
                      <p>Already have an account?{' '}
                        <button 
                          type="button"
                          className="link-button"
                          onClick={() => setIsSignUp(false)}
                        >
                          Login
                        </button>
                      </p>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showPinModal && (
        <div className="modal-overlay" onClick={() => setShowPinModal(false)}>
          <div className="pin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Enter PIN for @{selectedAccount}</h3>
              <button className="close-btn" onClick={() => setShowPinModal(false)}>×</button>
            </div>
            <form onSubmit={handleQuickLogin}>
              {error && <div className="error-message">{error}</div>}
              {isUpdatingKey && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '6px', color: 'var(--text-secondary)' }}>
                    New Hive Posting Key
                  </label>
                  <input
                    type="password"
                    className="pin-input"
                    value={newPostingKeyInput}
                    onChange={(e) => setNewPostingKeyInput(e.target.value)}
                    placeholder="5K... (New posting key)"
                    autoFocus
                    disabled={isLoading}
                    style={{ fontSize: '0.9rem', letterSpacing: 'normal' }}
                  />
                </div>
              )}
              <div className="form-group">
                {isUpdatingKey && (
                  <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '6px', color: 'var(--text-secondary)' }}>
                    Enter 6-digit PIN to secure key
                  </label>
                )}
                <input
                  type="password"
                  inputMode="numeric"
                  className="pin-input"
                  value={quickLoginPin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setQuickLoginPin(value);
                  }}
                  placeholder="••••••"
                  maxLength={6}
                  autoFocus={!isUpdatingKey}
                  disabled={isLoading}
                />
              </div>
              <button 
                type="submit"
                className="login-btn"
                disabled={isLoading || quickLoginPin.length !== 6 || (isUpdatingKey && !newPostingKeyInput.trim())}
              >
                {isLoading ? (isUpdatingKey ? 'Updating & Logging in...' : 'Logging in...') : (isUpdatingKey ? 'Update Key & Login' : 'Login')}
              </button>
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onClick={() => {
                    setIsUpdatingKey(!isUpdatingKey);
                    setError('');
                  }}
                >
                  {isUpdatingKey ? 'Cancel key update' : 'Posting key changed? Update it here'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default LoginModal;
